import React from 'react';

export interface Agreements {
  clubRules: boolean;
  codeOfConduct: boolean;
  waiver: boolean;
}

interface AgreementCheckboxesProps {
  agreements: Agreements;
  onChange: (agreements: Agreements) => void;
  showError?: boolean;
  errorMessage?: string;
}

const AgreementCheckboxes: React.FC<AgreementCheckboxesProps> = ({
  agreements,
  onChange,
  showError = false,
  errorMessage = 'You must accept all agreements to continue'
}) => {
  const handleCheckboxChange = (field: keyof Agreements) => {
    onChange({
      ...agreements,
      [field]: !agreements[field]
    });
  };

  const allAccepted = agreements.clubRules && agreements.codeOfConduct && agreements.waiver;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Required Agreements</h3>
      <p className="text-sm text-gray-600">
        You must read and accept all three agreements before completing registration.
      </p>

      <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
        {/* Club Rules */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="clubRules"
            checked={agreements.clubRules}
            onChange={() => handleCheckboxChange('clubRules')}
            className="mt-1 h-4 w-4 text-fishing-blue border-gray-300 rounded focus:ring-fishing-blue"
          />
          <label htmlFor="clubRules" className="ml-3 flex-1">
            <span className="text-sm font-medium text-gray-900">
              I have read and accept the{' '}
              <a
                href="/rules"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fishing-blue hover:underline"
              >
                Club Rules
              </a>
            </span>
          </label>
        </div>

        {/* Code of Conduct */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="codeOfConduct"
            checked={agreements.codeOfConduct}
            onChange={() => handleCheckboxChange('codeOfConduct')}
            className="mt-1 h-4 w-4 text-fishing-blue border-gray-300 rounded focus:ring-fishing-blue"
          />
          <label htmlFor="codeOfConduct" className="ml-3 flex-1">
            <span className="text-sm font-medium text-gray-900">
              I have read and accept the{' '}
              <a
                href="/conduct"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fishing-blue hover:underline"
              >
                Code of Conduct
              </a>
            </span>
          </label>
        </div>

        {/* Waiver & Release */}
        <div className="flex items-start">
          <input
            type="checkbox"
            id="waiver"
            checked={agreements.waiver}
            onChange={() => handleCheckboxChange('waiver')}
            className="mt-1 h-4 w-4 text-fishing-blue border-gray-300 rounded focus:ring-fishing-blue"
          />
          <label htmlFor="waiver" className="ml-3 flex-1">
            <span className="text-sm font-medium text-gray-900">
              I have read and accept the{' '}
              <a
                href="/waiver"
                target="_blank"
                rel="noopener noreferrer"
                className="text-fishing-blue hover:underline"
              >
                Waiver & Release
              </a>
            </span>
          </label>
        </div>
      </div>

      {/* Error Message */}
      {showError && !allAccepted && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      {/* Success Indicator */}
      {allAccepted && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <p className="text-sm text-green-800 flex items-center">
            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            All agreements accepted
          </p>
        </div>
      )}
    </div>
  );
};

export default AgreementCheckboxes;
